/* eslint-disable */
import React, { useEffect, useState } from "react";
import { X, Share, MoreVertical, Plus, Smartphone } from "lucide-react";

/**
 * Install-to-Home-Screen banner.
 * - On Android/Chrome we listen for the beforeinstallprompt event and offer 1-tap install
 * - On iOS Safari there's no programmatic install — we show step-by-step instructions
 * - Dismissed for 30 days via localStorage
 */
export default function InstallBanner() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [showIos, setShowIos] = useState(false);

  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;

  useEffect(() => {
    if (isStandalone) return;
    const dismissedAt = Number(localStorage.getItem("fr_install_dismissed") || 0);
    if (Date.now() - dismissedAt < 30 * 24 * 60 * 60 * 1000) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS doesn't fire beforeinstallprompt — show our own banner after a moment
    if (isIos) {
      const t = setTimeout(() => setShow(true), 1500);
      return () => {
        window.removeEventListener("beforeinstallprompt", handler);
        clearTimeout(t);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [isStandalone, isIos]);

  if (!show || isStandalone) return null;

  const dismiss = () => {
    localStorage.setItem("fr_install_dismissed", String(Date.now()));
    setShow(false);
    setShowIos(false);
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      setDeferred(null);
      setShow(false);
      if (outcome !== "accepted") dismiss();
    } else if (isIos) {
      setShowIos(true);
    }
  };

  if (showIos) {
    return (
      <div className="fixed inset-x-0 bottom-0 z-50 p-3 pointer-events-none">
        <div className="max-w-md mx-auto bg-white border border-gray-200 rounded-xl shadow-2xl p-5 pointer-events-auto" data-testid="install-ios-instructions">
          <div className="flex items-start justify-between mb-3">
            <div className="font-heading font-bold text-base">Install Fair Rideshare</div>
            <button onClick={dismiss} className="text-gray-400 hover:text-black" aria-label="Close">
              <X size={18} />
            </button>
          </div>
          <ol className="text-sm space-y-3 text-gray-700">
            <li className="flex items-start gap-3">
              <span className="font-mono text-xs bg-gray-100 rounded w-6 h-6 grid place-items-center shrink-0">1</span>
              <span>Tap the <Share size={14} className="inline mx-0.5 mb-0.5" /> Share button in Safari's toolbar.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="font-mono text-xs bg-gray-100 rounded w-6 h-6 grid place-items-center shrink-0">2</span>
              <span>Scroll down and tap <strong>Add to Home Screen</strong> <Plus size={14} className="inline mb-0.5" />.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="font-mono text-xs bg-gray-100 rounded w-6 h-6 grid place-items-center shrink-0">3</span>
              <span>Tap <strong>Add</strong> in the top-right.</span>
            </li>
          </ol>
          <p className="text-[11px] text-gray-500 mt-4">After that you'll have the Fair Rideshare icon on your home screen — tap to open instantly.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 p-3 pointer-events-none">
      <div className="max-w-md mx-auto bg-black text-white rounded-xl shadow-2xl p-4 pointer-events-auto flex items-center gap-3" data-testid="install-banner">
        <div className="w-10 h-10 rounded-md bg-amber-500 grid place-items-center shrink-0">
          <Smartphone size={18} className="text-black" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-semibold text-sm">Install Fair Rideshare</div>
          <div className="text-xs text-gray-300">Get the app icon on your home screen.</div>
        </div>
        <button
          onClick={install}
          className="bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs px-3 py-1.5 rounded-md whitespace-nowrap"
          data-testid="install-banner-install"
        >
          Install
        </button>
        <button onClick={dismiss} className="text-gray-400 hover:text-white p-1" aria-label="Dismiss">
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
